import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import useSnackbar from '@hooks/useSnackbar';
import { MESSAGE_BOX_VARIANT_SNACKBAR_ERROR } from '@components/2-molecules/MessageBox';
import {
  isRetryableDownloadFailure,
  isTransportError,
  reportDownloadFailure,
  triggerAuthDownload,
} from '@utils/authDownload';

/** A name, or a lookup for one that must resolve rather than reject. */
export type DownloadFilename = string | (() => Promise<string>);

export default function useAuthDownload() {
  const intl = useIntl();
  const { showSnackbar, hideSnackbar } = useSnackbar();
  const inFlightRef = useRef(false);
  const [downloading, setDownloading] = useState(false);

  const download = useCallback(
    async function run(url: string, nameOrLookup: DownloadFilename): Promise<void> {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setDownloading(true);
      let filename = typeof nameOrLookup === 'string' ? nameOrLookup : '';
      try {
        if (typeof nameOrLookup !== 'string') filename = await nameOrLookup();
        await triggerAuthDownload(url, filename, { observable: true });
      } catch (err) {
        reportDownloadFailure(err);
        const retryable = isRetryableDownloadFailure(err);
        showSnackbar({
          text: isTransportError(err)
            ? intl.formatMessage(
              {
                id: 'chat.artifact.downloadFailed',
                defaultMessage: "Couldn't download {name}. Check your connection and try again.",
              },
              { name: filename },
            )
            : intl.formatMessage(
              { id: 'chat.artifact.downloadFailedHttp', defaultMessage: "Couldn't download {name}." },
              { name: filename },
            ),
          variant: MESSAGE_BOX_VARIANT_SNACKBAR_ERROR,
          iconName: 'error_0',
          endingButtonTitle: retryable
            ? intl.formatMessage({ id: 'chat.artifact.downloadRetry', defaultMessage: 'Retry' })
            : undefined,
          onEndingButtonClick: retryable
            ? () => {
              hideSnackbar();
              void run(url, filename);
            }
            : undefined,
        });
      } finally {
        inFlightRef.current = false;
        setDownloading(false);
      }
    },
    [intl, showSnackbar, hideSnackbar],
  );

  return { download, downloading };
}
